/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { Icon } from '@adobe-commerce/elsie/components';
import { ChevronDown } from '@adobe-commerce/elsie/icons';
import { classes } from '@adobe-commerce/elsie/lib';
import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useEffect, useState } from 'preact/compat';

import '@adobe-commerce/elsie/components/Picker/Picker.css';

type PickerValue = string | null;

export interface PickerOption {
  value: PickerValue;
  text?: string;
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  disabled?: boolean;
}

export interface PickerProps
  extends Omit<HTMLAttributes<HTMLSelectElement>, 'value' | 'size' | 'icon'> {
  id?: string;
  name?: string;
  value?: PickerValue;
  placeholder?: string;
  variant?: 'primary' | 'secondary';
  size?: 'medium' | 'large';
  floatingLabel?: string;
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  options?: PickerOption[];
  defaultOption?: PickerOption;
  disabled?: boolean;
  error?: boolean;
  handleSelect?: (event: Event) => void;
}

function findSelectedValue(
  value?: PickerValue,
  defaultOption?: PickerOption,
  placeholder?: string,
  floatingLabel?: string,
  firstAvailableOption?: PickerOption
) {
  if (value) return value;
  if (defaultOption) return defaultOption.value;
  if (placeholder || floatingLabel) return '';
  if (firstAvailableOption) return firstAvailableOption.value;
  return null;
}

export const Picker: FunctionComponent<PickerProps> = ({
  name,
  value = null,
  options,
  variant = 'primary',
  floatingLabel,
  size = 'medium',
  handleSelect = () => {},
  disabled = false,
  error = false,
  placeholder,
  defaultOption,
  icon,
  className,
  id,
  ...props
}) => {
  const uniqueId = id ?? name ?? `dropin-picker-${Math.random().toString(36)}`;
  const isRequired = !!props?.required;

  // find the first option that is not disabled
  const firstAvailableOption = options?.find((option) => !option.disabled);

  const [selectedValue, setSelectedValue] = useState<PickerValue>(() => {
    return findSelectedValue(
      value,
      defaultOption,
      placeholder,
      floatingLabel,
      firstAvailableOption
    );
  });

  useEffect(() => {
    setSelectedValue(
      findSelectedValue(
        value,
        defaultOption,
        placeholder,
        floatingLabel,
        firstAvailableOption
      )
    );
  }, [value, defaultOption, placeholder, floatingLabel, firstAvailableOption]);

  const handleOptionClick = (event: Event) => {
    const { options, value } = event.target as HTMLSelectElement;

    for (const element of options) {
      if (element.selected) {
        setSelectedValue(value);
        handleSelect(event);
      }
    }
  };

  const optionsListItems = options?.map((option) => {
    const { value, text, disabled } = option;
    const selected = value === selectedValue;

    return (
      <option
        value={value as string}
        key={value}
        selected={selected}
        disabled={disabled}
        className={classes(['dropin-picker__option'])}
      >
        {text}
      </option>
    );
  });

  const isSelected = !!selectedValue;

  const hasToDisplayPlaceholder = () => {
    return (!isRequired || !isSelected) && (floatingLabel || placeholder);
  };

  return (
    <div
      className={classes([
        className,
        'dropin-picker',
        `dropin-picker__${size}`,
        ['dropin-picker__floating', !!floatingLabel],
        ['dropin-picker__selected', isSelected],
        ['dropin-picker__error', error],
        ['dropin-picker__disabled', disabled],
        ['dropin-picker__icon', icon],
      ])}
    >
      {icon && (
        // @ts-ignore
        <icon.type
          {...icon.props}
          className={'dropin-picker__icon--placeholder'}
        />
      )}

      <select
        id={uniqueId}
        className={classes([
          'dropin-picker__select',
          `dropin-picker__select--${variant}`,
          `dropin-picker__select--${size}`,
          ['dropin-picker__select--floating', !!floatingLabel],
        ])}
        name={name}
        aria-label={name}
        disabled={disabled}
        onChange={handleOptionClick}
        {...props}
      >
        {hasToDisplayPlaceholder() && (
          <option
            selected={!isSelected}
            value={''}
            key={value}
            className={classes([
              'dropin-picker__option dropin-picker__placeholder',
            ])}
          >
            {floatingLabel ?? placeholder}
          </option>
        )}

        {optionsListItems}
      </select>

      <Icon
        source={ChevronDown}
        size="24"
        stroke="2"
        className="dropin-picker__chevronDown"
      />

      {floatingLabel && isSelected && (
        <label
          htmlFor={id}
          className={classes(['dropin-picker__floatingLabel', !!floatingLabel])}
        >
          {floatingLabel}
        </label>
      )}
    </div>
  );
};
